import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Trophy,
  X,
  Users,
  Settings,
  CalendarDays,
  DollarSign,
  Award,
  FileText,
  ChevronRight,
  ChevronLeft,
  Plus,
  Trash2,
  LayoutGrid,
  Zap,
  MapPin
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { doc, collection, setDoc, getDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import { handleFirestoreError } from '../../lib/errorHandlers';
import { TournamentReward, Player, UserProfile } from '../../types';
import { logActivity } from '../../services/activityLogger';
import { auth } from '../../lib/firebase';
import { ALL_CITIES } from '../../data/locations';

interface TournamentWizardProps {
  db: any;
  getCollectionPath: (col: string) => string;
  onCancel: () => void;
  isTestMode?: boolean;
  organizerId: string;
  players?: Player[];
  userRole?: string;
  sedesPublicas?: UserProfile[];
}

export function TournamentWizard({ db, getCollectionPath, onCancel, isTestMode = false, organizerId, players = [], userRole, sedesPublicas = [] }: TournamentWizardProps) {
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const dateInputRef = useRef<HTMLInputElement>(null);

  // Step 1: Base Info
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<'liga' | 'torneo_eliminacion'>('liga');
  const [managementMode, setManagementMode] = useState<'autogestionado' | 'express'>('autogestionado');
  const [isMaster, setIsMaster] = useState(false);

  // Step 2: Structure
  const [maxTeams, setMaxTeams] = useState(16);
  const [category, setCategory] = useState('6ta');
  const [isSuma, setIsSuma] = useState(false);
  const [sumaValue, setSumaValue] = useState(13);
  const [gender, setGender] = useState<'masculino' | 'femenino' | 'mixto' | 'sin_restriccion'>('sin_restriccion');
  
  // Step 2.5: Location
  const [locationClubIds, setLocationClubIds] = useState<string[]>([organizerId]);
  const [showClubSelector, setShowClubSelector] = useState(false);

  // Step 3: Meta & Rewards
  const [startDate, setStartDate] = useState('');
  const [rulesText, setRulesText] = useState('');
  const [entryFee, setEntryFee] = useState(0);
  const [rewards, setRewards] = useState<TournamentReward[]>([
    { id: '1', rank: '1er Puesto', prize: '' },
    { id: '2', rank: '2do Puesto', prize: '' },
    { id: '3', rank: '3er Puesto', prize: '' }
  ]);

  const addReward = () => {
    const defaultRanks = ['1er Puesto', '2do Puesto', '3er Puesto', 'Semifinalistas', '4tos de Final', 'Octavos de Final'];
    const nextRank = defaultRanks[rewards.length] || '';
    setRewards([...rewards, { id: Math.random().toString(36).substr(2, 9), rank: nextRank, prize: '' }]);
  };

  const [expressBlocks, setExpressBlocks] = useState<{ id: string, date: string, startTime: string, endTime: string, courtIds: string[] }[]>([]);
  const addExpressBlock = () => {
    const allCourts = availableCourts.flatMap(ac => ac.courts);
    setExpressBlocks([...expressBlocks, { id: Math.random().toString(36).substr(2, 9), date: '', startTime: '14:00', endTime: '22:00', courtIds: allCourts }]);
  };
  const updateExpressBlock = (id: string, field: string, value: string) => {
    setExpressBlocks(expressBlocks.map(b => b.id === id ? { ...b, [field]: value } : b));
  };
  const removeExpressBlock = (id: string) => {
    setExpressBlocks(expressBlocks.filter(b => b.id !== id));
  };
  const toggleExpressBlockCourt = (blockId: string, courtName: string) => {
    setExpressBlocks(blocks => blocks.map(b => {
      if (b.id === blockId) {
        const hasCourt = b.courtIds.includes(courtName);
        return { 
          ...b, 
          courtIds: hasCourt ? b.courtIds.filter(c => c !== courtName) : [...b.courtIds, courtName] 
        };
      }
      return b;
    }));
  };

  const [availableCourts, setAvailableCourts] = useState<{clubId: string, clubName: string, courts: string[]}[]>([]);
  useEffect(() => {
    if (managementMode !== 'express' || locationClubIds.length === 0) return;
    const fetchCourts = async () => {
      const courtsData: {clubId: string, clubName: string, courts: string[]}[] = [];
      for (const clubId of locationClubIds) {
        try {
          const snap = await getDoc(doc(db, getCollectionPath('clubSchedules'), clubId));
          const clubName = sedesPublicas.find(s => s.uid === clubId)?.name || 'Sede';
          if (snap.exists() && snap.data().courts) {
            courtsData.push({ clubId, clubName, courts: snap.data().courts });
          }
        } catch (e) {
          console.error("Error fetching courts for club", clubId, e);
        }
      }
      setAvailableCourts(courtsData);
    };
    fetchCourts();
  }, [locationClubIds, managementMode, db, getCollectionPath, sedesPublicas]);

  const totalSteps = managementMode === 'express' ? 4 : 3;

  const removeReward = (id: string) => {
    setRewards(rewards.filter(r => r.id !== id));
  };

  const updateReward = (id: string, field: 'rank' | 'prize', value: string) => {
    setRewards(rewards.map(r => r.id === id ? { ...r, [field]: value } : r));
  };

  const toggleLocationClub = (clubId: string) => {
    if (locationClubIds.includes(clubId)) {
      setLocationClubIds(locationClubIds.filter(id => id !== clubId));
    } else {
      if (locationClubIds.length < 3) {
        setLocationClubIds([...locationClubIds, clubId]);
      } else {
        alert('Solo puedes seleccionar hasta 3 clubes.');
      }
    }
  };

  const handleCreate = async () => {
    if (!name) return;
    if (locationClubIds.length === 0) {
      alert('Debes seleccionar al menos un club para el torneo.');
      return;
    }
    setIsLoading(true);

    try {
      const newTournamentRef = doc(collection(db, getCollectionPath('tournaments')));
      const tId = newTournamentRef.id;

      await setDoc(newTournamentRef, {
        id: tId,
        name,
        type,
        category,
        gender,
        locationMode: 'clubs',
        locationClubIds: locationClubIds,
        locationCity: '',
        maxTeams: Number(maxTeams),
        status: 'Lobby',
        lobbyStatus: 'open',
        isArchived: false,
        championId: '',
        zones: [],
        brackets: [],
        rewards: rewards.filter(r => r.rank && r.prize),
        startDate: startDate || null,
        entryFee: Number(entryFee) || 0,
        description: description || '',
        rulesText: rulesText || '',
        registeredTeamIds: [],
        organizerId,
        managementMode,
        expressBlocks,
        createdAt: Date.now(),
        isTest: isTestMode,
        isMaster: isMaster
      });

      // Crear bloqueos automáticos en el calendario si es Express
      if (managementMode === 'express' && expressBlocks.length > 0) {
        for (const clubId of locationClubIds) {
          try {
            const scheduleRef = doc(db, getCollectionPath('clubSchedules'), clubId);
            const scheduleSnap = await getDoc(scheduleRef);
            if (scheduleSnap.exists()) {
              const scheduleData = scheduleSnap.data();
              const newOffDays = expressBlocks.filter(b => b.date).map(b => ({
                id: `block_${tId}_${b.id}`,
                date: b.date,
                startHour: b.startTime,
                endHour: b.endTime,
                allDay: false,
                reason: `Torneo: ${name}`,
                courts: b.courtIds // Puede ser vacío (todas) o las seleccionadas
              }));
              
              const currentOffDays = scheduleData.offDays || [];
              await updateDoc(scheduleRef, {
                offDays: [...currentOffDays, ...newOffDays]
              });
            }
          } catch (err) {
            console.error("Error creando bloqueos para el torneo:", err);
          }
        }
      }

      // Registro de Actividad
      const currentUser = auth.currentUser;
      const currentUserProfile = players.find(p => p.id === currentUser?.uid);
      const activeUserName = currentUserProfile?.name || currentUser?.displayName || currentUser?.email || 'Administrador';

      await logActivity(
        db,
        getCollectionPath,
        organizerId,
        activeUserName,
        userRole || 'gestor_torneos',
        `Creación de torneo manual: ${name} (${category})`
      );

      setIsLoading(false);
      onCancel();

      window.dispatchEvent(new CustomEvent('openAdminTournament', { detail: tId }));
      window.dispatchEvent(new CustomEvent('changeAdminTab', { detail: { tab: 'inscripciones', tournamentId: tId } }));
    } catch (error) {
      setIsLoading(false);
      handleFirestoreError(error, 'write', 'tournaments');
    }
  };

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 bg-black/30 backdrop-blur-sm overflow-y-auto animate-in fade-in duration-300" onClick={onCancel}>
      <div 
        className="bg-[#1a1c23]/95 border border-white/10 p-6 md:p-8 rounded-[2rem] relative shadow-[0_20px_50px_rgba(0,0,0,0.5)] overflow-hidden animate-in fade-in zoom-in-95 duration-500 max-w-lg w-full mx-auto ring-1 ring-white/5 my-auto"
        onClick={e => e.stopPropagation()}
      >
        {isLoading && (
        <div className="absolute inset-0 bg-black/80 z-50 flex flex-col items-center justify-center rounded-[2rem] backdrop-blur-md">
          <Zap className="w-16 h-16 theme-primary-text animate-pulse mb-4" />
          <span className="text-xl font-black theme-primary-text uppercase tracking-widest">Iniciando competición...</span>
        </div>
      )}

      <div className="relative flex justify-center items-start mb-6 pt-2">
        <div className="text-center px-8">
          <h3 className="text-xl md:text-2xl font-black uppercase tracking-tight theme-primary-text italic leading-tight">
            Asistente de creación de torneos
          </h3>
          <p className="text-xs md:text-sm font-bold text-white uppercase tracking-[0.3em] mt-2 mb-6">
            Paso {step} de {totalSteps}
          </p>
        </div>
        <button onClick={onCancel} className="absolute -right-2 md:-right-4 -top-2 opacity-40 hover:opacity-100 bg-white/5 hover:bg-white/10 p-2 rounded-full transition-all group shrink-0">
          <X size={20} className="group-hover:rotate-90 transition-transform" />
        </button>
      </div>

      <AnimatePresence mode="wait">
        {step === 1 && (
          <motion.div
            key="step1"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-4"
          >
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-widest theme-primary-text flex items-center justify-center gap-2">
                <Trophy size={14} /> Nombre del Torneo
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ej: Open Pro Padel 2026"
                className="w-full bg-white/5 border-2 border-white/5 rounded-xl p-3 font-black outline-none focus:theme-primary-border text-sm placeholder:opacity-10 transition-all shadow-inner"
              />
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-widest theme-primary-text flex items-center justify-center gap-2 opacity-50">
                <FileText size={14} /> Descripción (Opcional)
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Detalles breves del torneo..."
                className="w-full bg-white/5 border-2 border-white/5 rounded-xl p-3 font-bold outline-none focus:theme-primary-border text-xs min-h-[60px] resize-none"
              />
            </div>

            {userRole === 'superadmin' && (
              <div className="space-y-2 bg-amber-500/10 border border-amber-500/30 rounded-xl p-3">
                <label className="text-[10px] font-black uppercase tracking-widest text-amber-500 flex items-center justify-center gap-2">
                  <Trophy size={14} /> Categoría Master
                </label>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-[9px] text-white/70 leading-tight pr-4">
                    Al activar esta opción, los puntos de este torneo se sumarán directamente al <strong className="text-amber-500">Ranking General (Global)</strong>.
                  </span>
                  <button
                    onClick={() => setIsMaster(!isMaster)}
                    className={`relative w-10 h-5 rounded-full transition-colors flex-shrink-0 ${isMaster ? 'bg-amber-500' : 'bg-white/20'}`}
                  >
                    <div className={`absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full bg-white transition-transform ${isMaster ? 'translate-x-5' : 'translate-x-1'}`} />
                  </button>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-widest theme-primary-text flex items-center justify-center gap-2">
                <Settings size={14} /> Modalidad de Torneo
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setManagementMode('autogestionado')}
                  className={`group p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-1.5 ${managementMode === 'autogestionado' ? 'theme-primary-border theme-primary-bg text-black shadow-[0_0_20px_rgba(var(--glow-rgb),0.3)]' : 'border-white/5 bg-black/20 hover:border-white/10 text-white'}`}
                >
                  <CalendarDays className={`w-5 h-5 ${managementMode === 'autogestionado' ? 'text-black' : 'opacity-30'}`} />
                  <div className="text-center">
                    <span className={`block font-black text-[10px] uppercase ${managementMode === 'autogestionado' ? 'text-black' : ''}`}>Auto-Gestionado</span>
                    <span className={`text-[8px] leading-[1.1] block mt-0.5 ${managementMode === 'autogestionado' ? 'text-black/80 font-bold' : 'opacity-60'}`}>Los jugadores coordinan y cargan sus propios resultados</span>
                  </div>
                </button>

                <button
                  onClick={() => setManagementMode('express')}
                  className={`group p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-1.5 ${managementMode === 'express' ? 'theme-primary-border theme-primary-bg text-black shadow-[0_0_20px_rgba(var(--glow-rgb),0.3)]' : 'border-white/5 bg-black/20 hover:border-white/10 text-white'}`}
                >
                  <Zap className={`w-5 h-5 ${managementMode === 'express' ? 'text-black' : 'opacity-30'}`} />
                  <div className="text-center">
                    <span className={`block font-black text-[10px] uppercase ${managementMode === 'express' ? 'text-black' : ''}`}>Torneo Express</span>
                    <span className={`text-[8px] leading-[1.1] block mt-0.5 ${managementMode === 'express' ? 'text-black/80 font-bold' : 'opacity-60'}`}>Total control del organizador. Ideal para torneos cortos o de fin de semana</span>
                  </div>
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-widest theme-primary-text flex items-center justify-center gap-2">
                <Settings size={14} /> Formato de Competición
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setType('liga')}
                  className={`group p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-1.5 ${type === 'liga' ? 'theme-primary-border theme-primary-bg text-black shadow-[0_0_20px_rgba(var(--glow-rgb),0.3)]' : 'border-white/5 bg-black/20 hover:border-white/10 text-white'}`}
                >
                  <LayoutGrid className={`w-5 h-5 ${type === 'liga' ? 'text-black' : 'opacity-30'}`} />
                  <div className="text-center">
                    <span className={`block font-black text-[10px] uppercase ${type === 'liga' ? 'text-black' : ''}`}>Liga + Eliminación</span>
                  </div>
                </button>

                <button
                  onClick={() => setType('torneo_eliminacion')}
                  className={`group p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-1.5 ${type === 'torneo_eliminacion' ? 'theme-primary-border theme-primary-bg text-black shadow-[0_0_20px_rgba(var(--glow-rgb),0.3)]' : 'border-white/5 bg-black/20 hover:border-white/10 text-white'}`}
                >
                  <Trophy className={`w-5 h-5 ${type === 'torneo_eliminacion' ? 'text-black' : 'opacity-30'}`} />
                  <div className="text-center">
                    <span className={`block font-black text-[10px] uppercase ${type === 'torneo_eliminacion' ? 'text-black' : ''}`}>Eliminación Directa</span>
                  </div>
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {step === 2 && (
          <motion.div
            key="step2"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-4"
          >
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-widest theme-primary-text flex items-center justify-center gap-2">
                <Users size={14} /> Cupos de Inscripción (Estimado)
              </label>
              <div className="bg-white/5 border-2 border-white/5 rounded-2xl p-4 flex items-center gap-4">
                <div className="flex-1 px-1">
                  <input
                    type="range"
                    min="8"
                    max="32"
                    step="2"
                    value={maxTeams}
                    onChange={(e) => setMaxTeams(parseInt(e.target.value))}
                    className="w-full h-1.5 bg-black/40 border border-white/10 rounded-lg appearance-none cursor-pointer"
                    style={{ accentColor: 'var(--theme-primary, #fbbf24)' }}
                  />
                  <div className="flex justify-between text-[9px] font-black text-white/30 mt-2 px-1">
                    {[8, 12, 16, 20, 24, 28, 32].map(val => (
                      <span key={val} className={`transition-all duration-300 ${maxTeams >= val ? 'text-white/80' : ''} ${maxTeams === val ? 'text-amber-400 scale-125' : ''}`}>
                        {val}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div className="w-16 flex flex-col items-center justify-center border-l-2 border-white/5 pl-4 shrink-0">
                  <span className="text-2xl font-black text-white leading-none tracking-tighter">{maxTeams}</span>
                  <span className="text-[7px] font-bold text-white/40 uppercase tracking-widest mt-1">Cupos</span>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-widest theme-primary-text flex items-center justify-center gap-2">
                <Trophy size={14} /> Categoría
              </label>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-1.5">
                {['1era', '2da', '3ra', '4ta', '5ta', '6ta', '7ma', '8va'].map((cat) => (
                  <button
                    key={cat}
                    onClick={() => {
                      setIsSuma(false);
                      setCategory(cat);
                    }}
                    className={`py-2 rounded-lg font-black uppercase tracking-widest text-[9px] border-2 transition-all ${!isSuma && category === cat ? 'theme-primary-border theme-primary-bg text-black shadow-lg' : 'border-white/5 bg-black/20 text-white opacity-40 hover:opacity-100'}`}
                  >
                    {cat}
                  </button>
                ))}
                
                <button
                  onClick={() => {
                    setIsSuma(true);
                    setCategory('Suma ' + sumaValue);
                  }}
                  className={`py-2 rounded-lg font-black uppercase tracking-widest text-[9px] border-2 transition-all ${isSuma ? 'theme-primary-border theme-primary-bg text-black shadow-lg' : 'border-white/5 bg-black/20 text-white opacity-40 hover:opacity-100'}`}
                >
                  Suma
                </button>

                {isSuma && (
                  <div className="col-span-1 sm:col-span-1 flex items-center justify-between bg-black/30 border border-white/10 rounded-lg px-1 animate-in fade-in zoom-in-95">
                    <button 
                      onClick={() => {
                        const newVal = Math.max(2, sumaValue - 1);
                        setSumaValue(newVal);
                        setCategory('Suma ' + newVal);
                      }} 
                      className="w-6 h-6 rounded-md bg-white/5 hover:bg-white/10 flex items-center justify-center font-black transition-all text-xs"
                    >-</button>
                    <span className="font-black text-sm text-center w-full">{sumaValue}</span>
                    <button 
                      onClick={() => {
                        const newVal = Math.min(15, sumaValue + 1);
                        setSumaValue(newVal);
                        setCategory('Suma ' + newVal);
                      }}
                      className="w-6 h-6 rounded-md bg-white/5 hover:bg-white/10 flex items-center justify-center font-black transition-all text-xs"
                    >+</button>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-widest theme-primary-text flex items-center justify-center gap-2">
                <Users size={14} /> Género
              </label>
              <div className="flex gap-1.5">
                {[
                  { id: 'sin_restriccion', label: 'Sin Restricción', extra: 'Género Libre' },
                  { id: 'masculino', label: 'Caballeros' },
                  { id: 'femenino', label: 'Damas' },
                  { id: 'mixto', label: 'Mixto' }
                ].map((g) => (
                  <button
                    key={g.id}
                    onClick={() => setGender(g.id as any)}
                    className={`flex-1 py-1.5 md:py-2 rounded-lg font-black uppercase tracking-widest text-[9px] border-2 transition-all flex flex-col items-center justify-center gap-0.5 ${gender === g.id ? 'theme-primary-border theme-primary-bg text-black shadow-[0_0_15px_rgba(var(--glow-rgb),0.3)]' : 'border-white/5 bg-black/20 text-white opacity-40 hover:opacity-100'}`}
                  >
                    <span className="leading-none">{g.label}</span>
                    {g.extra && <span className={`text-[7px] ${gender === g.id ? 'text-black/70' : 'opacity-60'} normal-case font-bold leading-none`}>{g.extra}</span>}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-3 pt-3 border-t border-white/5">
              <label className="text-[10px] font-black uppercase tracking-widest theme-primary-text flex items-center justify-center gap-2">
                <MapPin size={14} /> Sede del Torneo
              </label>

              <div className="flex flex-col gap-2">
                {locationClubIds.map(clubId => {
                  const sede = sedesPublicas?.find(s => s.uid === clubId);
                  if (!sede) return null;
                  return (
                    <div key={clubId} className="flex items-center justify-between p-3 rounded-xl border border-white/10 bg-white/5">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-black/40 overflow-hidden flex-shrink-0">
                          {sede.photoUrl ? (
                            <img src={sede.photoUrl} alt={sede.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-[10px] text-white/20">S/F</div>
                          )}
                        </div>
                        <div>
                          <div className="text-xs font-bold text-white">{sede.name}</div>
                          <div className="text-[10px] text-white/50">{sede.city}</div>
                        </div>
                      </div>
                      {locationClubIds.length > 1 && (
                        <button onClick={() => toggleLocationClub(clubId)} className="p-2 text-white/40 hover:text-red-400 transition-colors">
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  );
                })}
                
                {locationClubIds.length < 3 && !showClubSelector && (
                  <button
                    onClick={() => setShowClubSelector(true)}
                    className="w-full py-3 rounded-xl border-2 border-dashed border-white/10 text-white/50 hover:text-white hover:border-white/30 hover:bg-white/5 transition-all text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2"
                  >
                    <Plus size={14} /> Añadir otra sede ({locationClubIds.length}/3)
                  </button>
                )}
              </div>

              {showClubSelector && (
                <div className="mt-2 p-4 rounded-xl border border-white/10 bg-black/20">
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-[10px] font-bold opacity-50 uppercase tracking-widest">Seleccionar Club</span>
                    <button onClick={() => setShowClubSelector(false)} className="text-white/40 hover:text-white">
                      <X size={14} />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[150px] overflow-y-auto custom-scrollbar pr-1">
                    {sedesPublicas?.filter(s => !locationClubIds.includes(s.uid)).map(sede => (
                      <button
                        key={sede.uid}
                        onClick={() => {
                          toggleLocationClub(sede.uid);
                          setShowClubSelector(false);
                        }}
                        className="flex items-center gap-2 p-2 rounded-xl border border-white/5 bg-white/5 hover:bg-white/10 hover:border-white/20 transition-all text-left"
                      >
                        <div className="w-8 h-8 rounded-lg bg-black/40 overflow-hidden flex-shrink-0">
                          {sede.photoUrl ? (
                            <img src={sede.photoUrl} alt={sede.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-[10px] text-white/20">S/F</div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[10px] font-bold text-white truncate">{sede.name}</div>
                          <div className="text-[8px] opacity-50 truncate">{sede.city}</div>
                        </div>
                      </button>
                    ))}
                    {(!sedesPublicas || sedesPublicas.filter(s => !locationClubIds.includes(s.uid)).length === 0) && (
                      <div className="col-span-1 sm:col-span-2 text-center text-xs opacity-50 py-4">No hay más clubes disponibles.</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {step === 3 && (
          <motion.div
            key="step3"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="space-y-3">
              <div className="relative flex justify-center items-center px-2 mb-4">
                <label className="text-sm font-black uppercase tracking-widest theme-primary-text flex items-center gap-2">
                  <Award size={16} /> Premios
                </label>
                <button onClick={addReward} className="absolute right-0 top-1/2 -translate-y-1/2 flex items-center gap-1.5 px-2 py-1 border border-white rounded-lg text-[8px] font-black uppercase tracking-widest text-white hover:bg-white/10 transition-colors">
                  <Plus size={12} /> Agregar
                </button>
              </div>
              <div className="space-y-2 max-h-[120px] overflow-y-auto pr-2 custom-scrollbar">
                {rewards.map((r) => (
                  <div key={r.id} className="flex gap-2">
                    <input
                      type="text"
                      value={r.rank}
                      placeholder="Posición"
                      onChange={(e) => updateReward(r.id, 'rank', e.target.value)}
                      className="w-1/4 bg-white/10 border border-white/10 rounded-lg p-2 text-[10px] font-black outline-none focus:theme-primary-border"
                    />
                    <input
                      type="text"
                      value={r.prize}
                      placeholder="Premio"
                      onChange={(e) => updateReward(r.id, 'prize', e.target.value)}
                      className="flex-1 bg-white/10 border border-white/10 rounded-lg p-2 text-[10px] font-black outline-none focus:theme-primary-border"
                    />
                    <button onClick={() => removeReward(r.id)} className="text-red-400 hover:text-red-300 px-1">
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 max-w-sm mx-auto">
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest opacity-50 block text-center">
                  Fecha de Inicio (Estimada)
                </label>
                <div className="relative group">
                  <div 
                    className="absolute left-3 top-1/2 -translate-y-1/2 z-20 cursor-pointer hover:scale-110 transition-transform"
                    onClick={(e) => {
                      e.stopPropagation();
                      dateInputRef.current?.showPicker();
                    }}
                  >
                    <CalendarDays size={16} className="theme-primary-text" />
                  </div>
                  <input
                    ref={dateInputRef}
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full bg-white/5 border border-white/5 rounded-xl p-3 pl-9 text-[10px] font-black outline-none focus:theme-primary-border appearance-none cursor-text text-center [&::-webkit-calendar-picker-indicator]:hidden"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest opacity-50 block text-center">
                  Costo Inscripción
                </label>
                <input
                  type="text"
                  value={entryFee ? new Intl.NumberFormat('es-AR').format(entryFee) : ''}
                  placeholder="$ 0"
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9]/g, '');
                    setEntryFee(val === '' ? 0 : parseInt(val));
                  }}
                  className="w-full bg-white/5 border border-white/5 rounded-xl p-3 text-[10px] font-black outline-none focus:theme-primary-border text-center"
                />
              </div>
            </div>

            <div className="space-y-3">
              <label className="text-xs font-black uppercase tracking-widest theme-primary-text flex items-center justify-center gap-2 opacity-50">
                <FileText size={14} /> Reglamento (Opcional)
              </label>
              <input
                type="text"
                value={rulesText}
                onChange={(e) => setRulesText(e.target.value)}
                placeholder="Link a Drive o descripción breve..."
                className="w-full bg-white/10 border border-white/10 rounded-xl p-3 text-[10px] font-bold outline-none focus:theme-primary-border"
              />
            </div>
          </motion.div>
        )}

        {step === 4 && managementMode === 'express' && (
          <motion.div
            key="step4"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="space-y-3">
              <div className="relative flex justify-center items-center px-2 mb-4">
                <label className="text-sm font-black uppercase tracking-widest theme-primary-text flex items-center gap-2">
                  <CalendarDays size={16} /> Bloqueos de Calendario
                </label>
                <button onClick={addExpressBlock} className="absolute right-0 top-1/2 -translate-y-1/2 flex items-center gap-1.5 px-2 py-1 border border-white rounded-lg text-[8px] font-black uppercase tracking-widest text-white hover:bg-white/10 transition-colors">
                  <Plus size={12} /> Agregar
                </button>
              </div>
              <p className="text-[10px] text-center text-white/50 px-4">
                Define los días y rangos horarios en los que se jugará el torneo. El sistema bloqueará estos horarios automáticamente en la grilla para que nadie más reserve.
              </p>
              
              <div className="space-y-3 max-h-[250px] overflow-y-auto pr-2 custom-scrollbar max-w-md mx-auto">
                {expressBlocks.map((b) => (
                  <div key={b.id} className="bg-white/5 border border-white/10 rounded-xl p-3 space-y-3 relative group">
                    <div className="flex gap-2 items-center">
                      <div className="flex-1">
                         <input type="date" value={b.date} onChange={(e) => updateExpressBlock(b.id, 'date', e.target.value)} className="w-full bg-black/30 border border-white/5 rounded-lg p-2 text-[10px] md:text-xs font-bold outline-none text-center focus:border-theme-primary transition-colors [color-scheme:dark] [&::-webkit-calendar-picker-indicator]:scale-[1.4] [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-70 hover:[&::-webkit-calendar-picker-indicator]:opacity-100 [&::-webkit-calendar-picker-indicator]:ml-1" />
                      </div>
                      <div className="flex gap-0.5 items-center bg-black/30 border border-white/5 rounded-lg overflow-hidden pr-1">
                         <input type="time" value={b.startTime} onChange={(e) => updateExpressBlock(b.id, 'startTime', e.target.value)} className="w-[85px] bg-transparent p-2 text-[10px] md:text-xs font-bold outline-none text-center [color-scheme:dark] [&::-webkit-calendar-picker-indicator]:scale-[1.4] [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-70 hover:[&::-webkit-calendar-picker-indicator]:opacity-100" />
                         <span className="text-white/30 text-[10px] font-black shrink-0">-</span>
                         <input type="time" value={b.endTime} onChange={(e) => updateExpressBlock(b.id, 'endTime', e.target.value)} className="w-[85px] bg-transparent p-2 text-[10px] md:text-xs font-bold outline-none text-center [color-scheme:dark] [&::-webkit-calendar-picker-indicator]:scale-[1.4] [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-70 hover:[&::-webkit-calendar-picker-indicator]:opacity-100" />
                      </div>
                      <button onClick={() => removeExpressBlock(b.id)} className="text-red-400 hover:text-red-300 p-2 opacity-50 hover:opacity-100 transition-opacity ml-1 bg-red-500/10 rounded-lg shrink-0">
                        <Trash2 size={16} />
                      </button>
                    </div>
                    {availableCourts.length > 0 && (
                      <div className="pt-2 border-t border-white/5 space-y-2">
                        <div className="text-[9px] font-bold text-white/50 uppercase tracking-widest text-center">
                          Canchas Afectadas
                        </div>
                        {availableCourts.map(ac => (
                          <div key={ac.clubId} className="bg-black/20 p-2 rounded-lg border border-white/5">
                            <div className="text-[9px] theme-primary-text font-black uppercase mb-1.5">{ac.clubName}</div>
                            <div className="flex flex-wrap gap-1.5">
                              {ac.courts.map(c => {
                                const isSelected = b.courtIds.includes(c);
                                return (
                                  <button
                                    key={c}
                                    onClick={() => toggleExpressBlockCourt(b.id, c)}
                                    className={`px-2 py-1 text-[9px] font-bold uppercase rounded-md border transition-all ${isSelected ? 'theme-primary-bg text-black border-transparent shadow-sm' : 'border-white/10 text-white/60 hover:border-white/30'}`}
                                  >
                                    {c}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                {expressBlocks.length === 0 && (
                  <div className="text-center py-6 text-white/20 text-[10px] uppercase font-bold border-2 border-dashed border-white/5 rounded-xl">
                    No has definido ningún bloque de horario.
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Navigation */}
      <div className="flex justify-between items-center mt-8 pt-6 border-t border-white/5">
        <button
          onClick={() => step > 1 ? setStep(step - 1) : onCancel()}
          className="flex items-center gap-2 px-6 py-3 rounded-xl font-bold uppercase tracking-widest opacity-40 hover:opacity-100 transition-all text-[10px]"
        >
          <ChevronLeft size={16} /> Atrás
        </button>

        {step < totalSteps ? (
          <button
            disabled={step === 1 && !name}
            onClick={() => {
              if (step === 2 && locationClubIds.length === 0) {
                alert('Debes seleccionar al menos 1 club sede para continuar.');
                return;
              }
              setStep(step + 1);
            }}
            className="group flex items-center gap-2 theme-primary-bg text-black px-8 py-3 rounded-xl font-black uppercase tracking-widest disabled:opacity-20 transition-all shadow-xl hover:scale-[1.02] active:scale-95 text-[10px]"
          >
            Siguiente <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </button>
        ) : (
          <button
            onClick={handleCreate}
            className="theme-accent-bg text-black px-10 py-3 rounded-xl font-black uppercase tracking-widest transition-all shadow-lg hover:scale-[1.02] active:scale-95 text-[10px] italic flex items-center gap-2"
          >
            <Trophy size={14} /> Finalizar y Crear
          </button>
        )}
      </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
